import React from "react";
import { Link } from "react-router-dom";
import { Heart, ShoppingBag, Handshake, Users, ArrowUpRight } from "lucide-react";
import { AUTOBOOKS_URL, PRINTIFY_URL } from "@/lib/config";

const CARDS = [
  {
    key: "donate",
    icon: Heart,
    eyebrow: "Give",
    title: "Donate",
    desc: "Fuel confidence, leadership, and life readiness with a secure gift through AutoBooks.",
    href: AUTOBOOKS_URL,
    external: true,
    cta: "Give Securely",
    testId: "support-card-donate",
  },
  {
    key: "shop",
    icon: ShoppingBag,
    eyebrow: "Wear It",
    title: "Shop AGG",
    desc: "Wear the mission. Every purchase invests in the next generation of confident young women.",
    href: PRINTIFY_URL,
    external: true,
    cta: "Shop the Store",
    testId: "support-card-shop",
  },
  {
    key: "partner",
    icon: Handshake,
    eyebrow: "Collaborate",
    title: "Partner With AGG",
    desc: "Schools, group homes, and organizations — bring AGG programming to your girls.",
    href: "/schools",
    external: false,
    cta: "Start a Partnership",
    testId: "support-card-partner",
  },
  {
    key: "serve",
    icon: Users,
    eyebrow: "Show Up",
    title: "Volunteer or Mentor",
    desc: "Be the woman who believes in her until she learns to believe in herself.",
    href: "/volunteer",
    external: false,
    cta: "Join the Team",
    testId: "support-card-serve",
  },
];

const CardInner = ({ card }) => {
  const Icon = card.icon;
  return (
    <>
      <div className="flex items-start justify-between">
        <div className="h-14 w-14 rounded-2xl bg-[#FFF5F9] flex items-center justify-center group-hover:bg-[#E94F9A] transition-colors duration-300">
          <Icon className="h-6 w-6 text-[#E94F9A] group-hover:text-white transition-colors duration-300" />
        </div>
        <ArrowUpRight className="h-5 w-5 text-[#E5E5E5] group-hover:text-[#E94F9A] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all duration-300" />
      </div>
      <div className="space-y-2 mt-6">
        <span className="font-sans text-[11px] font-semibold uppercase tracking-widest text-[#C8A96A]">
          {card.eyebrow}
        </span>
        <h3 className="font-serif text-xl font-bold text-[#4B1E5A]">{card.title}</h3>
        <p className="font-sans text-sm text-[#4A4A4A] leading-relaxed">{card.desc}</p>
      </div>
      <span className="mt-6 inline-flex items-center gap-1.5 font-sans text-xs font-semibold text-[#E94F9A]">
        {card.cta}
      </span>
    </>
  );
};

export const SupportAGG = () => {
  return (
    <section className="py-24 sm:py-32 bg-white" data-testid="support-agg-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-24">
        <div className="text-center max-w-2xl mx-auto space-y-4 mb-16">
          <span className="font-sans text-xs font-semibold uppercase tracking-widest text-[#C8A96A]">
            {"Join The Movement"}
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl font-bold text-[#4B1E5A] agg-heading agg-heading-center">
            {"Support AGG"}
          </h2>
          <p className="font-sans text-base text-[#4A4A4A] leading-relaxed">
            {"There's a place for everyone in this movement. However you choose to show up, you help a girl discover her worth, her voice, and her future."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {CARDS.map((card) =>
            card.external ? (
              <a
                key={card.key}
                href={card.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={card.testId}
                className="group flex flex-col bg-white border border-[#E5E5E5]/60 rounded-3xl p-7 hover:shadow-xl hover:-translate-y-2 hover:border-[#E94F9A]/30 transition-all duration-300 text-left"
              >
                <CardInner card={card} />
              </a>
            ) : (
              <Link
                key={card.key}
                to={card.href}
                data-testid={card.testId}
                className="group flex flex-col bg-white border border-[#E5E5E5]/60 rounded-3xl p-7 hover:shadow-xl hover:-translate-y-2 hover:border-[#E94F9A]/30 transition-all duration-300 text-left"
              >
                <CardInner card={card} />
              </Link>
            )
          )}
        </div>

        <div className="mt-16 text-center max-w-3xl mx-auto">
          <p className="font-serif text-xl sm:text-2xl italic text-[#4B1E5A] leading-relaxed">
            {"Every gift, every purchase, every hour, and every partnership helps a girl become a confident, compassionate, future-ready leader."}
          </p>
        </div>
      </div>
    </section>
  );
};
